import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { Decorator } from '@storybook/react'
import { useRequest } from 'ahooks'

import { DeeplinkFeatureKey } from '$extensionSrc/components/DeeplinkFeatureFlowContextProvider'
import { useActiveTabLanguage } from '$extensionSrc/data/activeTabLanguage'
import {
  BatchUpdateAutomationErrors,
  useBatchUpdateAutomationErrors,
} from '$extensionSrc/data/batchUpdateAutomationErrors'
import { useBatchUpdateRecommendationKeys } from '$extensionSrc/data/batchUpdateRecommendationKeys'
import { useLanguageLocked } from '$extensionSrc/data/languageLocked'
import { useDeeplinkFeatureKey } from '$extensionSrc/data/platform/deeplinkFeatureKey'
import { usePlatformName } from '$extensionSrc/data/platform/name'
import { usePlatform } from '$extensionSrc/data/platform/platform'
import { usePlatformUserAccountData } from '$extensionSrc/data/platform/platformUserAccountData'
import {
  RecommendationAutomationSpecs,
  useRecommendationAutomationSpecs,
} from '$extensionSrc/data/platform/recommendationAutomationSpecs'
import {
  RecommendationMetadata,
  useRecommendationMetadata,
} from '$extensionSrc/data/platform/recommendationMetadata'
import { useRecommendationSpecsWrapped } from '$extensionSrc/data/recommendationSpecs'
import { useShowLanguageWarning } from '$extensionSrc/data/showLanguageWarning'
import {
  setSidebarBonusRecommendationsOpen,
  useSidebarBonusRecommendations,
} from '$extensionSrc/data/sidebar/bonusRecommendationsOpen'
import { useDryRun, setDryRun } from '$extensionSrc/data/sidebar/dryRun'
import {
  SidebarFilters,
  clearSidebarFilters,
  toggleSidebarConcern,
  useSidebarFilters,
} from '$extensionSrc/data/sidebar/filters'
import {
  dismissFirstScanModal,
  getPromptForFirstScanModal,
  useFirstScanModalVisible,
} from '$extensionSrc/data/sidebar/firstScanModal'
import { useSidebarIframeHeight } from '$extensionSrc/data/sidebar/iframeHeight'
import {
  setSidebarViewScrollPosition,
  useSidebarScrollPosition,
} from '$extensionSrc/data/sidebar/scrollPosition'
import { useUpsellModal } from '$extensionSrc/data/sidebar/upsellModal'
import {
  SidebarView,
  setSidebarView,
  useSidebarView,
} from '$extensionSrc/data/sidebar/view'
import { AccessLevel } from '$extensionSrc/data/user/accessByPlatform'
import { isAutoscanOn } from '$extensionSrc/data/user/autoscanSettings'
import useWhereAmIRunning from '$extensionSrc/data/whereAmIRunning'
import useCurrentPlatform from '$extensionSrc/hooks/useCurrentPlatform'
import useCurrentPlatformRecommendationData from '$extensionSrc/hooks/useCurrentPlatformRecommendationData'
import RecommendationDisplayWrapper from '$extensionSrc/recommendations/recommendationDisplayWrapper'
import { RecommendationCardDisplayData } from '$extensionSrc/recommendations/recommendationProgressTypes'
import { sendGetRecommendationErrorStatus } from '$extensionSrc/utils/communication'
import {
  ConcernTag,
  ControlStageKey,
  PLATFORM,
  RecommendationKey,
} from '$extensionSrc/utils/enums'
import { isMobileOnlyPlatform } from '$extensionSrc/utils/platformUserData'

import { UserContext } from './UserContextProvider'

export type SidebarContextType = {
  activeTabId?: number
  autoscanOn: boolean | undefined
  batchUpdateAutomationErrors: BatchUpdateAutomationErrors | undefined
  batchUpdateRecommendationKeys: RecommendationKey[] | undefined
  bonusRecommendationsOpen: boolean | undefined
  checklistDrawerOpen: boolean | undefined
  clearFilters: (platform: PLATFORM) => Promise<void>
  completedRecommendations:
    | Partial<Record<RecommendationKey, boolean>>
    | undefined
  currentRecommendationCardIndex: number | undefined
  deeplinkFeatureKey: DeeplinkFeatureKey | null | undefined
  dismissFirstScanModal: (
    platform: PLATFORM,
    accessLevel: AccessLevel,
  ) => Promise<void>
  dryRun: boolean | undefined
  filters: SidebarFilters | undefined
  firstScanModalVisible: boolean | undefined
  hasCompletedFullScan: boolean | undefined
  hideUpsellModal: () => void
  iframeHeight: number | undefined
  isMobileOnly: boolean | undefined
  isPlaybookCompleted: boolean | undefined
  isUpsellModalVisible: boolean
  languageLocked: boolean | undefined
  lastScannedTimestamp: number | null | undefined
  lastSelectedPlatformUserId: string | null | undefined
  lastSelectedPlatformUsername: string | null | undefined
  platform: PLATFORM | null | undefined
  platformAccessLevel: AccessLevel | undefined
  platformName: string | null | undefined
  platformUserId: string | null | undefined
  platformUsername: string | null | undefined
  progress: { [key: string]: RecommendationCardDisplayData } | undefined
  recommendationAutomationSpecs: RecommendationAutomationSpecs | undefined
  recommendationCustomErrors:
    | Partial<
        Record<
          RecommendationKey,
          { title: string; body: string; stageKey: ControlStageKey } | null
        >
      >
    | {}
  recommendationKeys: RecommendationKey[] | undefined
  recommendationLastCompletedTimestamp: number | null | undefined
  recommendationMetadata: RecommendationMetadata | undefined
  recommendationSpecs:
    | Partial<Record<RecommendationKey, RecommendationDisplayWrapper>>
    | undefined
  scrollPosition: Partial<Record<SidebarView, number | string>> | undefined
  setBonusRecommendationsOpen: (
    platform: PLATFORM,
    open: boolean,
  ) => Promise<void>
  setChecklistDrawerOpen: Dispatch<SetStateAction<boolean>>
  setCurrentRecommendationCardIndex: Dispatch<
    SetStateAction<number | undefined>
  >
  setDryRun: (dryRun: boolean) => void
  setView: (platform: PLATFORM, view: SidebarView) => Promise<void>
  setViewScrollPosition: (
    platform: PLATFORM,
    view: SidebarView,
    scrollPosition: number,
  ) => Promise<void>
  showLanguageWarning: boolean | undefined
  showUpsellModal: () => void
  skippedRecommendations:
    | Partial<Record<RecommendationKey, boolean>>
    | undefined
  view: SidebarView | undefined
  toggleConcern: (platform: PLATFORM, concern: ConcernTag) => Promise<void>
}

export const defaultSidebarContext: SidebarContextType = {
  autoscanOn: undefined,
  batchUpdateAutomationErrors: undefined,
  batchUpdateRecommendationKeys: undefined,
  bonusRecommendationsOpen: undefined,
  checklistDrawerOpen: undefined,
  clearFilters: async (_platfrom) => {},
  completedRecommendations: undefined,
  currentRecommendationCardIndex: undefined,
  deeplinkFeatureKey: undefined,
  dismissFirstScanModal: async (_platform, _accessLevel) => {},
  dryRun: undefined,
  filters: undefined,
  firstScanModalVisible: undefined,
  hasCompletedFullScan: undefined,
  hideUpsellModal: () => {},
  iframeHeight: undefined,
  isMobileOnly: undefined,
  isPlaybookCompleted: undefined,
  isUpsellModalVisible: false,
  languageLocked: undefined,
  lastScannedTimestamp: undefined,
  lastSelectedPlatformUserId: undefined,
  lastSelectedPlatformUsername: undefined,
  platform: undefined,
  platformAccessLevel: undefined,
  platformName: undefined,
  platformUserId: undefined,
  platformUsername: undefined,
  progress: undefined,
  recommendationAutomationSpecs: undefined,
  recommendationCustomErrors: {},
  recommendationKeys: undefined,
  recommendationLastCompletedTimestamp: undefined,
  recommendationMetadata: undefined,
  recommendationSpecs: undefined,
  scrollPosition: undefined,
  setBonusRecommendationsOpen: async (_platform, _open) => {},
  setChecklistDrawerOpen: (_onOffFlag) => {},
  setCurrentRecommendationCardIndex: (_index) => {},
  toggleConcern: async (_platform, _concern) => {},
  setDryRun: (_dryRun) => {},
  setView: async (_platform, _view) => {},
  setViewScrollPosition: async (_platform, _view, _scrollPosition) => {},
  showLanguageWarning: undefined,
  showUpsellModal: () => {},
  skippedRecommendations: undefined,
  view: undefined,
}

export const SidebarContext = React.createContext<SidebarContextType>(
  defaultSidebarContext,
)

export default function SidebarContextProvider({ children }) {
  const { accessByPlatform, autoscanSettings } = useContext(UserContext)

  const { isSidePanel, currentTab } = useWhereAmIRunning()
  const activeTabId = currentTab?.id

  const platform = usePlatform()
  const platformName = usePlatformName(platform)
  const recommendationSpecs = useRecommendationSpecsWrapped(platform)
  const recommendationAutomationSpecs =
    useRecommendationAutomationSpecs(platform)
  const dryRun = useDryRun()
  const activeTabLanguage = useActiveTabLanguage(currentTab, isSidePanel)
  const languageLocked = useLanguageLocked(activeTabLanguage)
  const showLanguageWarning = useShowLanguageWarning(activeTabLanguage)
  const deeplinkFeatureKey = useDeeplinkFeatureKey(platform)
  const recommendationMetadata = useRecommendationMetadata(platform)
  const upsellModalData = useUpsellModal()
  const view = useSidebarView(platform)
  const filters = useSidebarFilters(platform)
  const bonusRecommendationsOpen = useSidebarBonusRecommendations(platform)
  const scrollPosition = useSidebarScrollPosition(platform)
  const iframeHeight = useSidebarIframeHeight()

  const { data: recommendationCustomErrors } = useRequest(
    sendGetRecommendationErrorStatus,
  )

  const [checklistDrawerOpen, setChecklistDrawerOpen] = useState<boolean>(false)
  const [currentRecommendationCardIndex, setCurrentRecommendationCardIndex] =
    useState<number | undefined>()

  // @TODO: Refactor into data
  const currentPlatform = useCurrentPlatform()

  const platformUserId = useMemo(() => {
    if (!currentPlatform.isLoaded) {
      // Translate into our "undefined means loading" paradigm
      return undefined
    }
    return currentPlatform?.platformUserId || null
  }, [currentPlatform.isLoaded, currentPlatform.platformUserId])

  const platformUserAccountData = usePlatformUserAccountData(
    platform,
    platformUserId,
  )

  const isMobileOnly = useMemo(() => isMobileOnlyPlatform(platform), [platform])

  // @TODO: Refactor into data
  const {
    isLoaded: currentPlatformRecommendationDataIsLoaded,
    isRecommendationCompletedByKey,
    isRecommendationSkippedByKey,
    isSetupCompleted,
    nChecklistCompleted,
    nChecklistTotal,
    progress,
    recommendationKeysToUse: recommendationKeys,
    recommendationLastUpdatedTimestamp,
    setupLastCompletedTimestamp,
  } = useCurrentPlatformRecommendationData(
    platform,
    platformUserId,
    platformUserAccountData,
  )

  // Results of last batch update on platform
  const batchUpdateAutomationErrors = useBatchUpdateAutomationErrors(platform)
  const batchUpdateRecommendationKeys =
    useBatchUpdateRecommendationKeys(platform)

  const hasCompletedFullScan: boolean | undefined = useMemo(() => {
    if (!currentPlatformRecommendationDataIsLoaded) {
      return undefined
    }
    if (progress === null) {
      return undefined
    }
    // Very hand-wavy way to determine if a full scan has been done: if we've
    // gathered more than one recommendation
    const numRecommendationsGathered = Object.keys(progress).filter((key) =>
      Boolean(progress[key].lastGatheredTimestamp),
    ).length
    return isSetupCompleted === true && numRecommendationsGathered > 1
  }, [currentPlatformRecommendationDataIsLoaded, isSetupCompleted, progress])

  const lastScannedTimestamp: number | null | undefined = useMemo(() => {
    if (!currentPlatformRecommendationDataIsLoaded) {
      return undefined
    }
    if (setupLastCompletedTimestamp === null) {
      return undefined
    }
    return setupLastCompletedTimestamp || null
  }, [currentPlatformRecommendationDataIsLoaded, setupLastCompletedTimestamp])

  const isPlaybookCompleted = useMemo(() => {
    if (!currentPlatformRecommendationDataIsLoaded) {
      return undefined
    }
    if (nChecklistCompleted === null || nChecklistTotal === null) {
      return false
    }
    if (nChecklistTotal === 0) {
      return false
    }
    return nChecklistCompleted === nChecklistTotal
  }, [
    currentPlatformRecommendationDataIsLoaded,
    nChecklistCompleted,
    nChecklistTotal,
  ])

  const lastSelectedPlatformUserId = useMemo(() => {
    if (currentPlatform.lastSelectedPlatformUserId === undefined) {
      // Not found
      return null
    }
    if (currentPlatform.lastSelectedPlatformUserId === null) {
      // Not yet loaded
      return undefined
    }
    return currentPlatform.lastSelectedPlatformUserId
  }, [currentPlatform.lastSelectedPlatformUserId])

  const lastSelectedPlatformUsername = useMemo(() => {
    if (currentPlatform.lastSelectedPlatformUsername === undefined) {
      // Not found
      return null
    }
    if (currentPlatform.lastSelectedPlatformUsername === null) {
      // Not yet loaded
      return undefined
    }
    return currentPlatform.lastSelectedPlatformUsername
  }, [currentPlatform.lastSelectedPlatformUsername])

  const platformUsername = useMemo(() => {
    if (platformUserAccountData === null) {
      return null
    }
    if (platformUserAccountData === undefined) {
      return undefined
    }
    return platformUserAccountData?.username || null
  }, [platformUserAccountData])

  const recommendationLastCompletedTimestamp = useMemo(() => {
    if (!currentPlatformRecommendationDataIsLoaded) {
      return undefined
    }
    return recommendationLastUpdatedTimestamp
  }, [
    currentPlatformRecommendationDataIsLoaded,
    recommendationLastUpdatedTimestamp,
  ])

  const skippedRecommendations = useMemo(() => {
    if (!currentPlatformRecommendationDataIsLoaded) {
      return undefined
    }
    return isRecommendationSkippedByKey
  }, [currentPlatformRecommendationDataIsLoaded, isRecommendationSkippedByKey])

  const completedRecommendations = useMemo(() => {
    if (!currentPlatformRecommendationDataIsLoaded) {
      return undefined
    }
    return isRecommendationCompletedByKey
  }, [
    currentPlatformRecommendationDataIsLoaded,
    isRecommendationCompletedByKey,
  ])

  const platformAccessLevel = useMemo(() => {
    if (accessByPlatform === undefined) {
      return undefined
    }
    return accessByPlatform?.[platform] || AccessLevel.PREVIEW
  }, [accessByPlatform, platform])

  const autoscanOn = useMemo(
    () => isAutoscanOn(autoscanSettings, platform, platformUserId),
    [autoscanSettings, platform, platformUserId],
  )

  const hasDeeplinkFeatureKey = useMemo(
    () => deeplinkFeatureKey !== null && deeplinkFeatureKey !== undefined,
    [deeplinkFeatureKey],
  )

  const firstScanModalVisible = useFirstScanModalVisible(
    platform,
    platformAccessLevel,
    platformUserId,
    hasDeeplinkFeatureKey,
    hasCompletedFullScan,
    showLanguageWarning,
    view,
  )

  const sidebarContext = useMemo<SidebarContextType>(() => {
    const sc: SidebarContextType = {
      activeTabId,
      autoscanOn,
      batchUpdateAutomationErrors,
      batchUpdateRecommendationKeys,
      bonusRecommendationsOpen,
      checklistDrawerOpen,
      clearFilters: clearSidebarFilters,
      completedRecommendations,
      currentRecommendationCardIndex,
      deeplinkFeatureKey,
      dismissFirstScanModal,
      dryRun,
      filters,
      firstScanModalVisible,
      hasCompletedFullScan,
      hideUpsellModal: upsellModalData.hideModal,
      iframeHeight,
      isMobileOnly,
      isPlaybookCompleted,
      isUpsellModalVisible: upsellModalData.isModalVisible,
      languageLocked,
      lastScannedTimestamp,
      lastSelectedPlatformUserId,
      lastSelectedPlatformUsername,
      platform,
      platformAccessLevel,
      platformName,
      platformUserId,
      platformUsername,
      progress,
      recommendationAutomationSpecs,
      recommendationKeys: recommendationKeys || undefined,
      recommendationCustomErrors,
      recommendationLastCompletedTimestamp,
      recommendationMetadata,
      recommendationSpecs,
      scrollPosition,
      setBonusRecommendationsOpen: setSidebarBonusRecommendationsOpen,
      setChecklistDrawerOpen,
      setCurrentRecommendationCardIndex,
      setDryRun,
      setView: setSidebarView,
      setViewScrollPosition: setSidebarViewScrollPosition,
      showLanguageWarning,
      showUpsellModal: upsellModalData.showModal,
      skippedRecommendations,
      view,
      toggleConcern: toggleSidebarConcern,
    }
    return sc
  }, [
    activeTabId,
    autoscanOn,
    batchUpdateAutomationErrors,
    batchUpdateRecommendationKeys,
    bonusRecommendationsOpen,
    checklistDrawerOpen,
    completedRecommendations,
    currentRecommendationCardIndex,
    deeplinkFeatureKey,
    dryRun,
    filters,
    firstScanModalVisible,
    hasCompletedFullScan,
    iframeHeight,
    isMobileOnly,
    isPlaybookCompleted,
    languageLocked,
    lastScannedTimestamp,
    lastSelectedPlatformUserId,
    lastSelectedPlatformUsername,
    platform,
    platformAccessLevel,
    platformName,
    platformUserId,
    platformUsername,
    progress,
    recommendationAutomationSpecs,
    recommendationCustomErrors,
    recommendationKeys,
    recommendationLastCompletedTimestamp,
    recommendationMetadata,
    recommendationSpecs,
    scrollPosition,
    showLanguageWarning,
    skippedRecommendations,
    view,
    upsellModalData.hideModal,
    upsellModalData.isModalVisible,
    upsellModalData.showModal,
  ])

  return (
    <SidebarContext.Provider value={sidebarContext}>
      {children}
    </SidebarContext.Provider>
  )
}

// eslint-disable-next-line react/function-component-definition
export const SidebarContextProviderDecorator: Decorator = (
  Story,
  { parameters: { sidebarContext: incomingSidebarContext } },
) => {
  const [view, setView_] = useState<SidebarView>(
    incomingSidebarContext?.view || 'Checklist',
  )
  const setView = useCallback(async (_platform: PLATFORM, v: SidebarView) => {
    setView_(v)
  }, [])

  const [filters, setFilters] = useState<SidebarFilters>(
    incomingSidebarContext?.filters || {},
  )

  const clearFilters = useCallback(async (_platform: PLATFORM) => {
    setFilters({})
  }, [])
  const toggleConcern = useCallback(
    async (_platform: PLATFORM, concern: ConcernTag) => {
      setFilters((currentFilters) => {
        const currentValue = currentFilters?.concerns?.[concern] || false
        return {
          ...(currentFilters || {}),
          concerns: {
            ...(currentFilters?.concerns || {}),
            [concern]: !currentValue,
          },
        }
      })
    },
    [],
  )

  const [checklistDrawerOpen, setChecklistDrawerOpen] = useState<boolean>(false)
  const [currentRecommendationCardIndex, setCurrentRecommendationCardIndex] =
    useState<number | undefined>()

  const [bonusRecommendationsOpen, setBonusRecommendationsOpen_] =
    useState<boolean>(false)
  const setBonusRecommendationsOpen = useCallback(
    async (_platform: PLATFORM, open: boolean) => {
      setBonusRecommendationsOpen_(open)
    },
    [],
  )

  const [scrollPosition, setScrollPosition] = useState<
    Partial<Record<SidebarView, number>>
  >({})
  const setViewScrollPosition = useCallback(
    async (_platform: PLATFORM, t: SidebarView, sp: number) => {
      setScrollPosition((currentScrollPosition) => ({
        ...currentScrollPosition,
        [t]: sp,
      }))
    },
    [],
  )

  const platform = useMemo(
    () =>
      incomingSidebarContext?.platform
        ? incomingSidebarContext.platform
        : undefined,
    [incomingSidebarContext?.platform],
  )

  const platformAccessLevel = useMemo(
    () =>
      incomingSidebarContext?.platformAccessLevel
        ? incomingSidebarContext.platformAccessLevel
        : AccessLevel.FULL,
    [incomingSidebarContext?.platformAccessLevel],
  )

  const autoscanOn = useMemo(
    () =>
      incomingSidebarContext?.autoscanOn
        ? incomingSidebarContext.autoscanOn
        : true,
    [incomingSidebarContext?.autoscanOn],
  )

  const platformUserId = useMemo(
    () =>
      incomingSidebarContext?.platformUserId
        ? incomingSidebarContext.platformUserId
        : null,
    [incomingSidebarContext?.platformUserId],
  )

  const hasCompletedFullScan = useMemo(
    () =>
      incomingSidebarContext?.hasCompletedFullScan !== undefined
        ? incomingSidebarContext.hasCompletedFullScan
        : true,
    [incomingSidebarContext?.hasCompletedFullScan],
  )

  const showLanguageWarning = useMemo(
    () =>
      incomingSidebarContext?.showLanguageWarning !== undefined
        ? incomingSidebarContext.showLanguageWarning
        : false,
    [incomingSidebarContext?.showLanguageWarning],
  )

  const deeplinkFeatureKey = useMemo(
    () =>
      incomingSidebarContext?.deeplinkFeatureKey
        ? incomingSidebarContext.deeplinkFeatureKey
        : null,
    [incomingSidebarContext?.deeplinkFeatureKey],
  )

  const [firstScanModalVisible, setFirstScanModalIsVisible] = useState<boolean>(
    () =>
      getPromptForFirstScanModal(
        platform,
        platformAccessLevel,
        platformUserId,
        deeplinkFeatureKey !== null && deeplinkFeatureKey !== undefined,
        hasCompletedFullScan,
        showLanguageWarning,
        view,
      ),
  )
  const dismissFirstScanModal_ = useCallback(
    async (_p: PLATFORM, _al: AccessLevel) => {
      setFirstScanModalIsVisible(false)
    },
    [],
  )

  const sidebarContext = useMemo(() => {
    const sc: SidebarContextType = {
      autoscanOn,
      batchUpdateAutomationErrors:
        incomingSidebarContext?.batchUpdateAutomationErrors
          ? incomingSidebarContext.batchUpdateAutomationErrors
          : [],
      batchUpdateRecommendationKeys:
        incomingSidebarContext?.batchUpdateRecommendationKeys
          ? incomingSidebarContext.batchUpdateRecommendationKeys
          : [],
      bonusRecommendationsOpen,
      checklistDrawerOpen,
      clearFilters,
      completedRecommendations: incomingSidebarContext?.completedRecommendations
        ? incomingSidebarContext.completedRecommendations
        : {},
      currentRecommendationCardIndex,
      deeplinkFeatureKey,
      dismissFirstScanModal: dismissFirstScanModal_,
      dryRun:
        incomingSidebarContext?.dryRun !== undefined
          ? incomingSidebarContext.dryRun
          : false,
      filters,
      firstScanModalVisible,
      hasCompletedFullScan,
      hideUpsellModal: () => {},
      iframeHeight:
        incomingSidebarContext?.iframeHeight !== undefined
          ? incomingSidebarContext.iframeHeight
          : 600,
      isMobileOnly:
        incomingSidebarContext?.isMobileOnly !== undefined
          ? incomingSidebarContext.isMobileOnly
          : false,
      isPlaybookCompleted:
        incomingSidebarContext?.isPlaybookCompleted !== undefined
          ? incomingSidebarContext.isPlaybookCompleted
          : false,
      isUpsellModalVisible:
        incomingSidebarContext?.isUpsellModalVisible !== undefined
          ? incomingSidebarContext.isUpsellModalVisible
          : false,
      languageLocked:
        incomingSidebarContext?.languageLocked !== undefined
          ? incomingSidebarContext.languageLocked
          : false,
      lastScannedTimestamp:
        incomingSidebarContext?.lastScannedTimestamp !== undefined
          ? incomingSidebarContext.lastScannedTimestamp
          : new Date().getTime(),
      lastSelectedPlatformUserId:
        incomingSidebarContext?.lastSelectedPlatformUserId
          ? incomingSidebarContext.lastSelectedPlatformUserId
          : null,
      lastSelectedPlatformUsername:
        incomingSidebarContext?.lastSelectedPlatformUsername
          ? incomingSidebarContext.lastSelectedPlatformUsername
          : null,
      platform,
      platformAccessLevel,
      platformName: incomingSidebarContext?.platformName
        ? incomingSidebarContext.platformName
        : null,
      platformUserId,
      platformUsername: incomingSidebarContext?.platformUsername
        ? incomingSidebarContext.platformUsername
        : null,
      progress: incomingSidebarContext?.progress
        ? incomingSidebarContext.progress
        : {},
      recommendationAutomationSpecs:
        incomingSidebarContext?.recommendationAutomationSpecs
          ? incomingSidebarContext.recommendationAutomationSpecs
          : {},
      recommendationCustomErrors:
        incomingSidebarContext?.recommendationCustomErrors
          ? incomingSidebarContext.recommendationCustomErrors
          : {},
      recommendationKeys: incomingSidebarContext?.recommendationKeys
        ? incomingSidebarContext.recommendationKeys
        : {},
      recommendationLastCompletedTimestamp:
        incomingSidebarContext?.recommendationLastCompletedTimestamp !==
        undefined
          ? incomingSidebarContext.recommendationLastCompletedTimestamp
          : null,
      recommendationMetadata: incomingSidebarContext?.recommendationMetadata
        ? incomingSidebarContext.recommendationMetadata
        : {},
      recommendationSpecs: incomingSidebarContext?.recommendationSpecs
        ? incomingSidebarContext.recommendationSpecs
        : [],
      scrollPosition,
      setBonusRecommendationsOpen,
      setChecklistDrawerOpen,
      setCurrentRecommendationCardIndex,
      setDryRun,
      setView,
      setViewScrollPosition,
      showLanguageWarning,
      showUpsellModal: () => {},
      skippedRecommendations: incomingSidebarContext?.skippedRecommendations
        ? incomingSidebarContext.skippedRecommendations
        : {},
      view,
      toggleConcern,
    }
    return sc
  }, [
    autoscanOn,
    bonusRecommendationsOpen,
    checklistDrawerOpen,
    clearFilters,
    deeplinkFeatureKey,
    currentRecommendationCardIndex,
    dismissFirstScanModal_,
    filters,
    firstScanModalVisible,
    hasCompletedFullScan,
    incomingSidebarContext?.batchUpdateAutomationErrors,
    incomingSidebarContext?.batchUpdateRecommendationKeys,
    incomingSidebarContext?.completedRecommendations,
    incomingSidebarContext?.dryRun,
    incomingSidebarContext?.iframeHeight,
    incomingSidebarContext?.isMobileOnly,
    incomingSidebarContext?.isPlaybookCompleted,
    incomingSidebarContext?.isUpsellModalVisible,
    incomingSidebarContext?.languageLocked,
    incomingSidebarContext?.lastScannedTimestamp,
    incomingSidebarContext?.lastSelectedPlatformUserId,
    incomingSidebarContext?.lastSelectedPlatformUsername,
    incomingSidebarContext?.platformName,
    incomingSidebarContext?.platformUsername,
    incomingSidebarContext?.progress,
    incomingSidebarContext?.recommendationAutomationSpecs,
    incomingSidebarContext?.recommendationCustomErrors,
    incomingSidebarContext?.recommendationKeys,
    incomingSidebarContext?.recommendationLastCompletedTimestamp,
    incomingSidebarContext?.recommendationMetadata,
    incomingSidebarContext?.recommendationSpecs,
    incomingSidebarContext?.skippedRecommendations,
    platform,
    platformAccessLevel,
    platformUserId,
    scrollPosition,
    setBonusRecommendationsOpen,
    setView,
    setViewScrollPosition,
    showLanguageWarning,
    view,
    toggleConcern,
  ])

  return (
    <SidebarContext.Provider value={sidebarContext}>
      <Story />
    </SidebarContext.Provider>
  )
}
